import { Capacitor, ExceptionCode } from '@capacitor/core';
import { FirebaseAppCheck, FirebaseAppCheckPlugin, GetTokenResult, InitializeOptions } from '@capacitor-firebase/app-check';

import { kzMark } from '@shared/util/kz-perf';
import { KzAppCheckCoolDown, kzAppCheckFreshWanted, kzAppCheckWithin } from './app-check-wait';

/**
 * App Check on Android (kzd-ac AC-2, PLAN-appcheck §4.2): a WebView on `https://localhost` is not a site reCAPTCHA can
 * vouch for, so the token comes from the device instead — Play Integrity, through the native App Check SDK, driven by
 * `@capacitor-firebase/app-check`. The web SDK still makes every call (Firestore, RTDB, Storage, the callables); its
 * `CustomProvider` asks this file for the token (`kzNativeTokenSource`, wired by `createKzAppCheck`). A token minted
 * for the Android app is valid for the whole project, so the web SDK's calls carry it as they would their own.
 *
 * - **Only where native Firebase is configured.** The plugin needs the native SDK, which starts only from the build's
 *   `google-services.json` (CI copies it in per env from GitLab Secure Files; none is in the repo). Without it the
 *   plugin fails to load (its constructor asks for the default FirebaseApp), Capacitor leaves it out of the bridge,
 *   and `Capacitor.isPluginAvailable` says so at start-up — synchronously, so `kzAppCheckPlan` starts nothing
 *   (`why: 'no-native'`) and start-up is exactly as before. A call made anyway is refused `UNIMPLEMENTED`: remembered,
 *   never asked again.
 * - **iOS waits until it ships** (ruling AC-R5): `KZ_NATIVE_APP_CHECK_SHELLS` is Android only. The plugin's pod is in
 *   place (`cap sync ios`), but without a `GoogleService-Info.plist` its first call raises an Objective-C exception
 *   (the default App Check instance with no FirebaseApp) that JavaScript cannot catch — it would crash the app. iOS
 *   joins once the plist and the App Attest capability are in (see the AC-2 report).
 * - **Debug:** under the web's own rule — `environment.appCheck.allowDebug` (dev builds) and a debug token stored on
 *   this device — the plugin's debug provider replaces Play Integrity (a sideloaded debug APK fails Play Integrity).
 *   On Android the stored token only switches it on: the native debug provider keeps its own secret, printed once to
 *   logcat (`adb logcat | grep DebugAppCheckProvider`), and that secret is what the Firebase console must allow.
 *   Where the plugin is unavailable, a stored token falls back to the web SDK's debug mode, as before AC-2.
 * - **Fail open, as on the web:** each request is bounded (`KZ_APP_CHECK_WAIT_MS`), a failure is remembered for the
 *   shared growing cool-down (`app-check-wait.ts`), and a request that cannot get a token rejects — the SDK then
 *   sends the call without one (report-only: it goes through).
 * - `isTokenAutoRefreshEnabled` on both sides: the native SDK keeps its token fresh (ruling AC-R2: 12 h for Play
 *   Integrity, set in the console), and the web SDK asks again before its copy expires — the plugin answers from the
 *   native cache, so Play Integrity is asked about once per TTL (PLAN §6, the quota).
 */

/** The plugin's name on the bridge (`@CapacitorPlugin(name = "FirebaseAppCheck")`). */
export const KZ_NATIVE_APP_CHECK_PLUGIN = 'FirebaseAppCheck';

/** Where native App Check is wired: Android. iOS waits until it ships (ruling AC-R5; see the file comment). */
export const KZ_NATIVE_APP_CHECK_SHELLS: readonly string[] = ['android'];

/**
 * How long a native token is trusted when the plugin answers without its expiry (the plugin documents it for Android
 * and iOS, so this is a guard only): short, so the web SDK asks again soon — the plugin answers from its own cache.
 */
export const KZ_NATIVE_TOKEN_FALLBACK_MS = 5 * 60_000;

/** The plugin methods this file drives. */
export type KzNativeAppCheckPlugin = Pick<FirebaseAppCheckPlugin, 'initialize' | 'getToken'>;

/**
 * The plugin and its availability, read at call time so a spec can stub them (as `KZ_APP_CHECK_SDK`). In a browser
 * the plugin's web implementation would answer `isPluginAvailable` — never asked there: only Android shells are.
 */
export const KZ_NATIVE_APP_CHECK: { plugin: KzNativeAppCheckPlugin; isAvailable: () => boolean } = {
  plugin: FirebaseAppCheck,
  isAvailable: () => Capacitor.isPluginAvailable(KZ_NATIVE_APP_CHECK_PLUGIN),
};

/** Android's start: the plugin, with its debug provider (`debug`) or Play Integrity. */
export interface KzNativeAppCheckStart {
  readonly start: true;
  readonly mode: 'native';
  readonly debug: boolean;
}

/**
 * This shell's native App Check, for `kzAppCheckPlan` (after the backend check): `null` when the shell is not one of
 * `KZ_NATIVE_APP_CHECK_SHELLS` — the web, iOS and the desktop decide as before — and on Android:
 * - the plugin is on the bridge (native Firebase configured): start it, `debug` per the web's rule (`debugAllowed`:
 *   `allowDebug` and a stored debug token);
 * - it is not: nothing starts (`no-native`) — unless debug is allowed, then `null`: the web SDK's debug mode, as
 *   before AC-2 (a developer's debug APK built without `google-services.json`).
 */
export function kzNativeAppCheckPlan(
  platform: string,
  debugAllowed: boolean,
): KzNativeAppCheckStart | { readonly start: false; readonly why: 'no-native' } | null {
  if (!KZ_NATIVE_APP_CHECK_SHELLS.includes(platform)) return null;
  if (nativeAvailable()) return { start: true, mode: 'native', debug: debugAllowed };
  return debugAllowed ? null : { start: false, why: 'no-native' };
}

/** `KZ_NATIVE_APP_CHECK.isAvailable()`, and `false` if even that throws. */
function nativeAvailable(): boolean {
  try {
    return KZ_NATIVE_APP_CHECK.isAvailable() === true;
  } catch {
    return false;
  }
}

/** A token as `CustomProvider` takes it (the SDK's `AppCheckToken`). */
export interface KzNativeAppCheckToken {
  readonly token: string;
  readonly expireTimeMillis: number;
}

export interface KzNativeTokenSourceOptions {
  /** How long the plugin's start, then its answer, may take (`KZ_APP_CHECK_WAIT_MS`). */
  readonly waitMs: number;
  /** The plugin (default `KZ_NATIVE_APP_CHECK.plugin`, read per request), for the spec. */
  readonly plugin?: KzNativeAppCheckPlugin;
  /** The clock, for the spec. */
  readonly now?: () => number;
}

/** The plugin is not on the bridge: Capacitor's `UNIMPLEMENTED` (no native Firebase config, or not in the build). */
export function isNativeUnimplemented(err: unknown): boolean {
  return (err as { code?: unknown } | null | undefined)?.code === ExceptionCode.Unimplemented;
}

let warnedUnimplemented = false;
/** The one-time warning when the plugin is not on the bridge. Exported for the spec. */
export function warnNativeUnimplemented(): void {
  if (warnedUnimplemented) return;
  warnedUnimplemented = true;
  kzMark('appcheck', 'no-native-plugin');
  console.warn('[kz] App Check: the native plugin is not available (no native Firebase config?); calls go without its token.');
}

/**
 * The native token source behind Android's `CustomProvider` (see `createKzAppCheck`). Per request:
 * 1. after `UNIMPLEMENTED` (the plugin is not on the bridge), it rejects at once, for good;
 * 2. during a cool-down (`KzAppCheckCoolDown`: 60 s, doubling to 10 min, reset by a token), it rejects at once;
 * 3. the plugin is started once — `initialize({ isTokenAutoRefreshEnabled: true, debugToken? })` — at most `waitMs`
 *    (a start that failed is tried again by the next request after the cool-down; one still on its way is waited
 *    for again);
 * 4. then asked for its token — at most `waitMs`: `forceRefresh: false` (the native cache, refreshed by the native
 *    SDK), except inside a fresh window (`kzAppCheckFreshWanted`, below).
 * Any other failure (a plugin error, Play Integrity refusing or offline, a timeout, an empty answer) starts the
 * cool-down. Nothing here throws outside the returned promise.
 *
 * Limited-use tokens (AC-5): the web SDK's `getLimitedUseToken` asks a `CustomProvider` the same `getToken`, and the
 * plugin (8.5) has no limited-use call — so the cached native token would go out again, and a second signup or reset
 * from this phone within the TTL would be consumed as a replay. `kzLimitedUseCallable` opens a fresh window around
 * those calls (app-check-wait.ts); inside it this source asks `forceRefresh: true`, a new Play Integrity attestation
 * and a token never sent before (it then becomes the native cache).
 */
export function kzNativeTokenSource(
  plan: KzNativeAppCheckStart,
  opts: KzNativeTokenSourceOptions,
): () => Promise<KzNativeAppCheckToken> {
  const now = opts.now ?? (() => Date.now());
  const plugin = (): KzNativeAppCheckPlugin => opts.plugin ?? KZ_NATIVE_APP_CHECK.plugin;
  const cool = new KzAppCheckCoolDown(now);
  const options: InitializeOptions = plan.debug ? { isTokenAutoRefreshEnabled: true, debugToken: true } : { isTokenAutoRefreshEnabled: true };
  let started = false;
  let starting: Promise<void> | null = null;
  let unusable = false;

  return async () => {
    if (unusable) throw new Error('App Check: the native plugin is not available.');
    if (cool.cooling) throw new Error('App Check: the native plugin failed a moment ago; not asked again yet.');
    const forceRefresh = kzAppCheckFreshWanted();
    try {
      if (!started) {
        if (!starting) {
          const attempt: Promise<void> = Promise.resolve()
            .then(() => plugin().initialize(options))
            .catch((err: unknown) => {
              if (starting === attempt) starting = null;
              throw err;
            });
          starting = attempt;
        }
        await kzAppCheckWithin(starting, opts.waitMs, 'The native App Check plugin did not start in time.');
        started = true;
      }
      const asked: Promise<GetTokenResult> = Promise.resolve().then(() => plugin().getToken({ forceRefresh }));
      const result = await kzAppCheckWithin(asked, opts.waitMs, 'The native App Check plugin did not answer in time.');
      if (typeof result?.token !== 'string' || !result.token) throw new Error('The native App Check plugin answered without a token.');
      cool.reset();
      const at = now();
      const expires = result.expireTimeMillis;
      return { token: result.token, expireTimeMillis: typeof expires === 'number' && expires > at ? expires : at + KZ_NATIVE_TOKEN_FALLBACK_MS };
    } catch (err) {
      if (isNativeUnimplemented(err)) {
        unusable = true;
        warnNativeUnimplemented();
      } else {
        cool.fail();
      }
      throw err;
    }
  };
}
